"use client";

import React, { memo, useMemo, useState, useCallback } from 'react';
import { motion } from "framer-motion";
import { useComponentTranslations, type ServicesTranslationKey } from "@/app/components/LanguageProvider";

const PricingCard = memo(function PricingCard({
  planKey,
  planSubKey,
  priceKey,
  featuresKey,
  whenKey,
  bulletKeys,
  bulletFallbacks,
  conditionKey,
  conditionFallback,
  index,
}: {
  planKey: ServicesTranslationKey;
  planSubKey: ServicesTranslationKey;
  priceKey: ServicesTranslationKey;
  featuresKey: ServicesTranslationKey;
  whenKey: ServicesTranslationKey;
  bulletKeys: ServicesTranslationKey[];
  bulletFallbacks: string[];
  conditionKey: ServicesTranslationKey;
  conditionFallback: string;
  index: number;
}) {
  const { t } = useComponentTranslations("Services");
  const [isFlipped, setIsFlipped] = useState(false);

  const plan = useMemo(() => t(planKey, 'Hourly Rate'), [t, planKey]);
  const planSub = useMemo(() => t(planSubKey, ''), [t, planSubKey]);
  const price = useMemo(() => t(priceKey, 'from $50 / hour'), [t, priceKey]);
  const features = useMemo(() => t(featuresKey, 'Flexibility, pay-as-you-go'), [t, featuresKey]);
  const when = useMemo(() => t(whenKey, 'When it suits:'), [t, whenKey]);
  const bullets = useMemo(() => bulletKeys.map((bk, i) => t(bk, bulletFallbacks[i])), [t, bulletKeys, bulletFallbacks]);
  const condition = useMemo(() => t(conditionKey, conditionFallback), [t, conditionKey, conditionFallback]);

  const handleClick = useCallback(() => {
    setIsFlipped(prev => !prev);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsFlipped(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsFlipped(false);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="group h-[440px] md:h-[520px] [perspective:1000px] cursor-pointer snap-start shrink-0 w-72 md:w-auto"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={`relative w-full h-full [transition:transform_0.6s] [transform-style:preserve-3d] ${isFlipped ? '[transform:rotateY(180deg)]' : ''}`}>
        {/* Front side */}
        <div className="absolute inset-0 [backface-visibility:hidden] border border-white/10 bg-white/5 p-6 flex flex-col items-center text-center">
          <div className="flex-1 flex flex-col justify-center">
            <h3 className="text-lg md:text-xl font-bold text-white">{plan}</h3>
            <p className="text-xs md:text-sm text-white/60 mt-1 mb-3 md:mb-4">{planSub}</p>
            <p className="text-xl md:text-2xl font-bold text-white">{price}</p>
          </div>
          <div className="mt-auto">
            <p className="text-xs md:text-sm text-white/60">{features}</p>
          </div>
        </div>

        {/* Back side */}
        <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] border border-white/10 bg-white/5 p-6 flex flex-col justify-center text-center overflow-y-visible">
          <h4 className="text-xs md:text-sm font-semibold text-white/80 mb-2 md:mb-3">{when}</h4>
          <ul className="text-xs md:text-sm text-white/60 space-y-2">
            {bullets.map((bullet, i) => (
              <li key={i} className="text-center">{bullet}</li>
            ))}
          </ul>
          <p className="text-xs text-white/40 pt-3 mt-3 border-t border-white/10">
            {condition}
          </p>
        </div>
      </div>
    </motion.div>
  );
});

export default function PricingCards() {
  const { t } = useComponentTranslations("Services");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      viewport={{ once: true, amount: 0.3 }}
      className="mt-20"
    >
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-white mb-4">
          {t('pricingTitle', 'Pricing Plans')}
        </h2>
        <p className="text-sm text-white/60 max-w-3xl mx-auto">
          {t('pricingSubtitle', 'The choice of cooperation model depends on the project stage, clarity of the technical specification, and the required development intensity.')}
        </p>
        <p className="text-xs text-white/40 max-w-2xl mx-auto mt-2">
          {t('pricingHint', 'Hover over a card to learn more')}
        </p>
      </div>

      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-2 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:pb-0 mb-12">
        <PricingCard
          planKey="pricing1Plan"
          planSubKey="pricing1PlanSub"
          priceKey="pricing1Price"
          featuresKey="pricing1Features"
          whenKey="pricing1When"
          bulletKeys={["pricing1Bullet1", "pricing1Bullet2", "pricing1Bullet3"]}
          bulletFallbacks={[
            "Minor fixes and support: When you need to periodically make changes to an existing project.",
            "Undefined scope: When requirements change on the fly and it is impossible to create a final specification.",
            "R&D and prototyping: Hypothesis testing where speed of start matters more than final budget.",
          ]}
          conditionKey="pricing1Condition"
          conditionFallback="With hourly billing: Reports are provided weekly based on the task tracker."
          index={0}
        />
        <PricingCard
          planKey="pricing2Plan"
          planSubKey="pricing2PlanSub"
          priceKey="pricing2Price"
          featuresKey="pricing2Features"
          whenKey="pricing2When"
          bulletKeys={["pricing2Bullet1", "pricing2Bullet2", "pricing2Bullet3"]}
          bulletFallbacks={[
            "Long-term development: When the project requires full-time involvement (160+ hours per month). This is more cost-effective than hourly.",
            "Product in active phase: When the specialist becomes part of your team, knows all the architectural nuances, and is available during working hours.",
            "Scaling: When you need to quickly strengthen your current team with a proven developer.",
          ]}
          conditionKey="pricing2Condition"
          conditionFallback="With monthly rent: You get a fixed number of developer hours per month. Overtime is paid separately or carried over."
          index={1}
        />
        <PricingCard
          planKey="pricing3Plan"
          planSubKey="pricing3PlanSub"
          priceKey="pricing3Price"
          featuresKey="pricing3Features"
          whenKey="pricing3When"
          bulletKeys={["pricing3Bullet1", "pricing3Bullet2", "pricing3Bullet3"]}
          bulletFallbacks={[
            "MVP with a strict budget: When there is a clear specification, project boundaries, and a fixed amount that cannot be exceeded.",
            "Standard solutions: Creating a landing page, corporate website, or standard service where the scope of work is known in advance.",
            "One-off tasks: When you need a ready-made turnkey tool without needing to get involved in the development process.",
          ]}
          conditionKey="pricing3Condition"
          conditionFallback="With project-based billing: Any changes to the specification after the start are recorded as additional tasks and paid separately."
          index={2}
        />
      </div>
      <div className="flex justify-center gap-1.5 md:hidden mt-4">
        <span className="w-6 h-1 rounded-full bg-white/30" />
        <span className="w-1.5 h-1 rounded-full bg-white/15" />
        <span className="w-1.5 h-1 rounded-full bg-white/15" />
      </div>
    </motion.div>
  );
}
